/**
 * iter1113 Phase 2 - Archived Bank Accounts Section
 *
 * Rendered inside Settings > Banking below the main Bank Accounts card.
 * Provides:
 *   - Read-only list of accounts the user has archived
 *   - Reactivate button (POST /api/bank-accounts/{id}/reactivate) which
 *     returns the row to the active list as MANUAL and surfaces the gap
 *     window so the user knows to reconnect or upload a bridge statement.
 *   - Permanently delete button, gated to journal_count === 0 rows only
 *     (the backend enforces the same guard - button is a courtesy).
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Archive, RotateCcw, Trash2, AlertCircle, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ArchivedBankAccountsSection = ({ onChange }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const fetchArchived = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/bank-accounts/archived`);
      setRows(res.data?.archived || []);
    } catch (e) {
      // Silent - a fresh account or unauthenticated call would 404;
      // the section just stays empty in that case.
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchArchived();
  }, [fetchArchived]);

  const handleReactivate = async (row) => {
    setBusyId(row.id);
    try {
      const res = await axios.post(`${API}/bank-accounts/${row.id}/reactivate`);
      const gap = res.data?.gap;
      if (gap?.gap_days) {
        toast.success(
          `${row.bank_name || 'Account'} reactivated. ${gap.gap_days} day${gap.gap_days !== 1 ? 's' : ''} missing (${gap.gap_start} to ${gap.gap_end}). Open the bank to reconnect or upload a statement.`,
          { duration: 8000 }
        );
      } else {
        toast.success(`${row.bank_name || 'Account'} reactivated as a manual account.`);
      }
      fetchArchived();
      if (onChange) onChange();
    } catch (e) {
      toast.error(e?.normalised?.message || e?.response?.data?.detail || 'Failed to reactivate');
    } finally {
      setBusyId(null);
    }
  };

  const handlePermanentDelete = async (row) => {
    if (!row.deletable) return;
    if (!window.confirm(
      `Permanently delete ${row.bank_name || 'this bank account'}? This cannot be undone. Only empty accounts with zero journal entries can be deleted.`
    )) return;
    setBusyId(row.id);
    try {
      await axios.delete(`${API}/bank-accounts/${row.id}`);
      toast.success(`${row.bank_name || 'Account'} permanently deleted.`);
      fetchArchived();
      if (onChange) onChange();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.error_code === 'has_journal_history') {
        toast.error(detail.message || 'Cannot delete - this account has posted journal entries.');
      } else {
        toast.error(e?.normalised?.message || 'Failed to delete');
      }
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <Card className="mt-6" data-testid="archived-banks-loading">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  if (!rows.length) {
    return null; // Hide the section entirely when there is nothing to show.
  }

  return (
    <Card className="mt-6" data-testid="archived-banks-section">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Archive className="w-5 h-5 text-muted-foreground" />
          Archived bank accounts
        </CardTitle>
        <CardDescription>
          Archived accounts stay in your reports but cannot be used for new transactions,
          invoices, bills, transfers or statement uploads. Reactivate to bring one back
          as a manual account.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {rows.map((row) => (
            <div
              key={row.id}
              className="flex items-center justify-between p-3 border rounded-lg bg-muted/20"
              data-testid={`archived-bank-row-${row.id}`}
            >
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-10 h-10 rounded-lg bg-slate-200 flex items-center justify-center flex-shrink-0">
                  <Archive className="w-5 h-5 text-slate-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">
                    {row.bank_name || row.institution_name || 'Bank account'}
                    {row.mask && (
                      <span className="text-muted-foreground ml-1">····{row.mask}</span>
                    )}
                  </div>
                  <div className="text-xs text-muted-foreground flex items-center gap-2 flex-wrap">
                    {row.archived_at && (
                      <span>Archived {new Date(row.archived_at).toLocaleDateString()}</span>
                    )}
                    {row.archived_from_provider && (
                      <Badge variant="outline" className="text-[10px] h-4 capitalize">
                        was {row.archived_from_provider}
                      </Badge>
                    )}
                    <span>·</span>
                    <span>
                      {row.journal_count > 0
                        ? `${row.journal_count} journal entr${row.journal_count !== 1 ? 'ies' : 'y'}`
                        : 'No journal entries'}
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleReactivate(row)}
                  disabled={busyId === row.id}
                  data-testid={`reactivate-bank-${row.id}`}
                  className="text-[#108981] border-[#108981]/40 hover:bg-[#108981]/5"
                >
                  {busyId === row.id ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                  )}
                  Reactivate
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handlePermanentDelete(row)}
                  disabled={!row.deletable || busyId === row.id}
                  title={
                    row.deletable
                      ? 'Permanently delete this empty account'
                      : `${row.journal_count} posted journal entr${row.journal_count !== 1 ? 'ies' : 'y'} - cannot be deleted`
                  }
                  data-testid={`delete-archived-bank-${row.id}`}
                  className={row.deletable ? 'text-red-600 hover:bg-red-50' : 'text-muted-foreground opacity-50 cursor-not-allowed'}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 flex items-start gap-2 text-xs text-muted-foreground p-3 rounded-lg bg-amber-50 border border-amber-200">
          <AlertCircle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
          <span>
            Permanently delete is only available for empty accounts. Accounts with
            posted journal entries can be archived but never deleted, so your audit
            history stays intact.
          </span>
        </div>
      </CardContent>
    </Card>
  );
};

export default ArchivedBankAccountsSection;
